import React, { useState, useEffect } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { Apiary, Hive } from '../types';
import { VoiceRecorder } from '../features/media/VoiceRecorder';
import { useBeeBaseAlerts } from '../features/beebase/useBeeBaseAlerts';
import { useBeeBaseRisk } from '../features/beebase/useBeeBaseRisk';
import { weatherService } from '../services/weatherService';
import { useHiveWeather } from '../hooks/useHiveWeather';
import EnhancedAIAnalysis from './EnhancedAIAnalysis';
import './EnhancedInspectionForm.css';

interface EnhancedInspectionFormProps {
  selectedApiary: Apiary | null;
  selectedHive: Hive | null;
  onClose: () => void;
  onSave?: () => void;
}

interface InspectionFormData {
  apiaryId: string;
  hiveId: string;
  date: string;
  weather: string;
  temperature: string;
  queenPresent: boolean;
  queenMarked: boolean;
  queenClipped: boolean;
  broodPattern: string;
  broodFrames: number;
  disease: string;
  diseaseNotes: string;
  treatment: string;
  feeding: string;
  equipment: string;
  notes: string;
  severity: 'Low' | 'Medium' | 'High';
  populationStrength: number;
  temperament: string;
  honeySupers: number;
  frameCondition: string;
}

const EnhancedInspectionForm: React.FC<EnhancedInspectionFormProps> = ({
  selectedApiary,
  selectedHive,
  onClose,
  onSave
}) => {
  const { user } = useAuth();
  const apiaryLocation = selectedApiary ? { lat: selectedApiary.lat, lng: selectedApiary.lng } : null;
  const { alerts } = useBeeBaseAlerts({});
  const { weatherCodeToDescription } = useHiveWeather();
  
  // Get risk matches for the selected apiary
  const riskMatches = useBeeBaseRisk(
    selectedApiary ? [selectedApiary] : [], 
    alerts, 
    10
  );

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<InspectionFormData>({
    apiaryId: selectedApiary?.id || '',
    hiveId: selectedHive?.id || '',
    date: new Date().toISOString().split('T')[0],
    weather: '',
    temperature: '',
    queenPresent: true,
    queenMarked: false,
    queenClipped: false,
    broodPattern: 'Good',
    broodFrames: 0,
    disease: '',
    diseaseNotes: '',
    treatment: '',
    feeding: '',
    equipment: '',
    notes: '',
    severity: 'Low',
    populationStrength: 3,
    temperament: 'Calm',
    honeySupers: 0,
    frameCondition: 'Good'
  });

  const [submitting, setSubmitting] = useState(false);
  const [showAIInspection, setShowAIInspection] = useState(false);
  const [completionScore, setCompletionScore] = useState(0);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [voiceBlob, setVoiceBlob] = useState<Blob | null>(null);
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [aiAnalysisData, setAiAnalysisData] = useState<any>(null);

  const steps = [
    { id: 1, title: 'Basic Info', icon: '📅', description: 'Date, weather, and conditions' },
    { id: 2, title: 'Queen Status', icon: '👑', description: 'Queen presence and performance' },
    { id: 3, title: 'Colony Health', icon: '🐝', description: 'Population, brood, and temperament' },
    { id: 4, title: 'Disease Check', icon: '🦠', description: 'Health monitoring and diseases' },
    { id: 5, title: 'Management', icon: '🛠️', description: 'Treatments and equipment' },
    { id: 6, title: 'Documentation', icon: '📸', description: 'Photos, notes, and voice records' }
  ];

  // Calculate completion score - only count user-filled fields
  useEffect(() => {
    const calculateScore = () => {
      // Only count fields that user has meaningfully filled (not defaults)
      const userFilledFields = [
        // Weather and temperature (not pre-filled)
        formData.weather && formData.weather !== '',
        formData.temperature && formData.temperature !== '',
        
        // Queen status details (only if user explicitly marked/clipped)
        formData.queenMarked,
        formData.queenClipped,
        
        // Brood frames (only if user entered a number > 0)
        formData.broodFrames > 0,
        
        // Disease info (only if user selected/entered something)
        formData.disease && formData.disease !== '',
        formData.diseaseNotes && formData.diseaseNotes !== '',
        
        // Management actions (only if user entered something)
        formData.treatment && formData.treatment !== '',
        formData.feeding && formData.feeding !== '',
        formData.equipment && formData.equipment !== '',
        
        // Notes (only if user wrote something)
        formData.notes && formData.notes !== ''
      ];
      
      // Count how many fields are actually filled by user
      const filledCount = userFilledFields.filter(Boolean).length;
      
      // Start with base 20% for having the form open with basic info
      const baseScore = 20;
      const remainingScore = 80;
      
      // Calculate additional score based on user input
      const additionalScore = (filledCount / userFilledFields.length) * remainingScore;
      
      return Math.round(baseScore + additionalScore);
    };
    
    setCompletionScore(calculateScore());
  }, [formData]);

  // Auto-fill weather data
  useEffect(() => {
    const fillWeatherFromHive = () => {
      const hiveWeather = selectedHive?.weather;
      
      if (hiveWeather?.current && !formData.weather && !formData.temperature) {
        const temp = `${Math.round(hiveWeather.current.temperature_2m)}`;
        const condition = weatherCodeToDescription(hiveWeather.current.weather_code);
        
        setFormData(prev => ({
          ...prev,
          weather: condition,
          temperature: temp
        }));
        return true;
      }
      return false;
    };

    if (!fillWeatherFromHive() && apiaryLocation?.lat && apiaryLocation?.lng) {
      weatherService.getWeatherForInspection(apiaryLocation.lat, apiaryLocation.lng)
        .then(weatherData => {
          if (weatherData.success) {
            setFormData(prev => ({
              ...prev,
              weather: weatherData.weather,
              temperature: weatherData.temperature?.replace('°C', '') || ''
            }));
          }
        })
        .catch(console.error);
    }
  }, [selectedHive?.id, selectedHive?.weather, apiaryLocation?.lat, apiaryLocation?.lng, formData.weather, formData.temperature, weatherCodeToDescription]);

  const handleInputChange = (field: keyof InspectionFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setPhotoPreview(e.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !selectedApiary || !selectedHive) return;

    setSubmitting(true);
    try {
      let imageUrls: string[] = [];
      
      // Upload images if any were provided through AI analysis
      if (uploadedImages.length > 0) {
        console.log('Uploading inspection images...');
        const uploadPromises = uploadedImages.map(async (file, index) => {
          const timestamp = Date.now();
          const fileName = `inspection_${selectedHive.id}_${timestamp}_${index}.${file.name.split('.').pop()}`;
          const storageRef = ref(storage, `inspections/${user.uid}/${fileName}`);
          
          await uploadBytes(storageRef, file);
          return await getDownloadURL(storageRef);
        });
        
        imageUrls = await Promise.all(uploadPromises);
        console.log('Images uploaded successfully:', imageUrls);
      }

      // Create inspection document with enhanced data
      const inspectionRecord = {
        date: formData.date,
        weather: formData.weather ? `${formData.weather}${formData.temperature ? `, ${formData.temperature}°C` : ''}` : null,
        queen_sighted: formData.queenPresent,
        temperament: formData.temperament as 'Calm' | 'Agitated' | 'Aggressive',
        varroa_check: formData.treatment?.toLowerCase().includes('varroa') || formData.notes?.toLowerCase().includes('varroa') || false,
        health_rating: formData.populationStrength,
        notes: generateInspectionNotes(),
        hive_id: selectedHive.id,
        user_id: user.uid,
        created_at: new Date().toISOString(),
        // Enhanced inspection data
        brood_pattern: formData.broodPattern || null,
        brood_frames: formData.broodFrames || null,
        disease: formData.disease || null,
        treatment: formData.treatment || null,
        feeding: formData.feeding || null,
        equipment: formData.equipment || null,
        severity: formData.severity || null,
        honey_supers: formData.honeySupers || null,
        frame_condition: formData.frameCondition || null,
        // AI analysis data if available
        ai_analysis: aiAnalysisData?.raw_ai_response || aiAnalysisData?.ai_analysis || aiAnalysisData?.notes || null,
        // Image URLs
        images: imageUrls,
        image_count: imageUrls.length
      };

      await addDoc(collection(db, 'inspections'), inspectionRecord);
      console.log('Enhanced inspection saved successfully with', imageUrls.length, 'images');

      if (onSave) onSave();
      onClose();
    } catch (error) {
      console.error('Error saving enhanced inspection:', error);
      alert('Error saving inspection. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const generateInspectionNotes = (): string => {
    const notes = [];
    
    // Basic info
    notes.push(`🏺 Hive: ${selectedHive?.name} (${selectedApiary?.name})`);
    
    // Queen status
    if (formData.queenPresent) {
      notes.push(`👑 Queen: Present${formData.queenMarked ? ' (marked)' : ''}${formData.queenClipped ? ' (clipped)' : ''}`);
    } else {
      notes.push(`👑 Queen: Not sighted`);
    }
    
    // Brood and population
    notes.push(`🥚 Brood: ${formData.broodPattern} pattern, ${formData.broodFrames} frames`);
    notes.push(`🐝 Population: ${formData.populationStrength}/5, ${formData.temperament}`);
    
    // Equipment and management
    if (formData.honeySupers > 0) notes.push(`🍯 Honey supers: ${formData.honeySupers}`);
    if (formData.frameCondition) notes.push(`🖼️ Frame condition: ${formData.frameCondition}`);
    
    // Health and treatment
    if (formData.disease) notes.push(`🦠 Disease: ${formData.disease}`);
    if (formData.diseaseNotes) notes.push(`Disease notes: ${formData.diseaseNotes}`);
    if (formData.treatment) notes.push(`💊 Treatment: ${formData.treatment}`);
    if (formData.feeding) notes.push(`🍯 Feeding: ${formData.feeding}`);
    if (formData.equipment) notes.push(`🛠️ Equipment: ${formData.equipment}`);
    
    // Additional notes
    if (formData.notes) notes.push(`📝 Notes: ${formData.notes}`);
    
    return notes.join('\n');
  };

  const handleAIAnalysisComplete = (analysis: any) => {
    setAiAnalysisData(analysis); // Store the complete AI analysis
    setFormData(prev => ({
      ...prev,
      queenPresent: analysis.queen_spotted || prev.queenPresent,
      disease: analysis.disease_indicators?.length > 0 ? analysis.disease_indicators.join(', ') : (prev.disease || ''),
      notes: prev.notes + (prev.notes ? '\n\n' : '') + 'AI Analysis:\n' + (analysis.raw_ai_response || analysis.ai_analysis || analysis.notes || 'Analysis completed'),
      weather: analysis.weather || prev.weather,
      broodPattern: analysis.brood_pattern_quality || prev.broodPattern,
    }));
    setShowAIInspection(false);
  };

  const handleImageUpload = (files: File[]) => {
    setUploadedImages(files);
  };

  const nextStep = () => {
    if (currentStep < steps.length) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const goToStep = (step: number) => {
    setCurrentStep(step);
  };

  if (!selectedApiary || !selectedHive) return null;

  const currentStepData = steps.find(s => s.id === currentStep);

  return (
    <div className="enhanced-inspection-form">
      <div className="form-container">
        {/* Header */}
        <div className="form-header">
          <div className="header-content">
            <h2>🔍 Enhanced Hive Inspection</h2>
            <div className="hive-info">
              <span>🏡 {selectedApiary.name}</span>
              <span>🏺 {selectedHive.name}</span>
            </div>
          </div>
          <div className="header-actions">
            <button 
              onClick={() => setShowAIInspection(!showAIInspection)}
              className="ai-toggle-btn"
            >
              {showAIInspection ? '📝 Manual Form' : '🤖 AI Analysis'}
            </button>
            <button onClick={onClose} className="close-btn">✕</button>
          </div>
        </div>

        {/* Progress Indicator */}
        
        {/* AI Analysis Call-to-Action Banner */}
        {!showAIInspection && (
          <div className="ai-banner-cta">
            <div className="ai-banner-content">
              <div className="ai-banner-icon">🤖✨</div>
              <div className="ai-banner-text">
                <h3>AI Analysis of your Hive Inspection</h3>
                <p>Get instant AI-powered insights from your hive photos. Upload images and receive detailed analysis of bee health, behavior, and potential issues.</p>
              </div>
              <button 
                onClick={() => setShowAIInspection(true)}
                className="ai-banner-btn"
              >
                <span>🚀 Start AI Analysis</span>
              </button>
            </div>
          </div>
        )}
        <div className="progress-section">
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${(currentStep / steps.length) * 100}%` }}
              ></div>
            </div>
            <div className="completion-score">
              <span className="score-label">Completion</span>
              <span className="score-value">{completionScore}%</span>
            </div>
          </div>
          
          <div className="steps-indicator">
            {steps.map((step) => (
              <div 
                key={step.id}
                className={`step-item ${currentStep === step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}
                onClick={() => goToStep(step.id)}
              >
                <div className="step-icon">{step.icon}</div>
                <div className="step-content">
                  <div className="step-title">{step.title}</div>
                  <div className="step-description">{step.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Risk Assessment Alert */}
        {riskMatches.length > 0 && (
          <div className="risk-alert">
            <h4>⚠️ Risk Assessment Alert</h4>
            <p>
              There are {riskMatches.length} disease alert(s) within 10km of this apiary:
            </p>
            {riskMatches.map((match, index) => (
              <div key={index} className={`risk-item risk-${match.risk.toLowerCase()}`}>
                • {match.disease} - {match.distanceKm}km away ({match.risk} risk)
              </div>
            ))}
          </div>
        )}

        {/* AI Analysis Mode */}
        {showAIInspection ? (
          <EnhancedAIAnalysis 
            hiveId={selectedHive?.id || 'unknown'}
            onAnalysisComplete={handleAIAnalysisComplete} 
          />
        ) : (
          <form onSubmit={handleSubmit} className="inspection-form-content">
            {/* Step Content */}
            <div className="step-content-container">
              <div className="step-header">
                <h3>
                  {currentStepData?.icon} {currentStepData?.title}
                </h3>
                <p>{currentStepData?.description}</p>
              </div>

              {/* Step 1: Basic Info */}
              {currentStep === 1 && (
                <div className="form-step">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>📅 Inspection Date</label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => handleInputChange('date', e.target.value)}
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>🌤️ Weather Conditions</label>
                      <select
                        value={formData.weather}
                        onChange={(e) => handleInputChange('weather', e.target.value)}
                      >
                        <option value="">Select weather</option>
                        <option value="Sunny">☀️ Sunny</option>
                        <option value="Partly Cloudy">⛅ Partly Cloudy</option>
                        <option value="Cloudy">☁️ Cloudy</option>
                        <option value="Rainy">🌧️ Rainy</option>
                        <option value="Windy">💨 Windy</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label>🌡️ Temperature (°C)</label>
                      <input
                        type="number"
                        value={formData.temperature}
                        onChange={(e) => handleInputChange('temperature', e.target.value)}
                        placeholder="18"
                        min="-10"
                        max="40"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Queen Status */}
              {currentStep === 2 && (
                <div className="form-step">
                  <div className="form-grid">
                    <div className="form-group checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.queenPresent}
                          onChange={(e) => handleInputChange('queenPresent', e.target.checked)}
                        />
                        <span className="checkmark"></span>
                        👑 Queen Present
                      </label>
                    </div>
                    
                    {formData.queenPresent && (
                      <>
                        <div className="form-group checkbox-group">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={formData.queenMarked}
                              onChange={(e) => handleInputChange('queenMarked', e.target.checked)}
                            />
                            <span className="checkmark"></span>
                            🏷️ Queen Marked
                          </label>
                        </div>
                        
                        <div className="form-group checkbox-group">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={formData.queenClipped}
                              onChange={(e) => handleInputChange('queenClipped', e.target.checked)}
                            />
                            <span className="checkmark"></span>
                            ✂️ Queen Wing Clipped
                          </label>
                        </div>
                      </>
                    )}
                    
                    <div className="form-group">
                      <label>🥚 Brood Pattern Quality</label>
                      <select
                        value={formData.broodPattern}
                        onChange={(e) => handleInputChange('broodPattern', e.target.value)}
                      >
                        <option value="Excellent">Excellent - Solid, compact pattern</option>
                        <option value="Good">Good - Mostly solid with few gaps</option>
                        <option value="Fair">Fair - Some gaps, irregular pattern</option>
                        <option value="Poor">Poor - Many gaps, spotty pattern</option>
                        <option value="None">None - No brood present</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label>📊 Brood Frames</label>
                      <input
                        type="number"
                        value={formData.broodFrames}
                        onChange={(e) => handleInputChange('broodFrames', parseInt(e.target.value) || 0)}
                        min="0"
                        max="20"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Colony Health */}
              {currentStep === 3 && (
                <div className="form-step">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>🐝 Population Strength (1-5)</label>
                      <div className="rating-container">
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <button
                            key={rating}
                            type="button"
                            className={`rating-btn ${formData.populationStrength >= rating ? 'active' : ''}`}
                            onClick={() => handleInputChange('populationStrength', rating)}
                          >
                            🐝
                          </button>
                        ))}
                        <span className="rating-label">
                          {formData.populationStrength === 1 && 'Very Weak'}
                          {formData.populationStrength === 2 && 'Weak'}
                          {formData.populationStrength === 3 && 'Moderate'}
                          {formData.populationStrength === 4 && 'Strong'}
                          {formData.populationStrength === 5 && 'Very Strong'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label>😤 Bee Temperament</label>
                      <select
                        value={formData.temperament}
                        onChange={(e) => handleInputChange('temperament', e.target.value)}
                      >
                        <option value="Calm">😌 Calm - Easy to work with</option>
                        <option value="Normal">🐝 Normal - Typical behavior</option>
                        <option value="Agitated">😠 Agitated - Somewhat defensive</option>
                        <option value="Aggressive">😡 Aggressive - Very defensive</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label>🍯 Honey Supers</label>
                      <input
                        type="number"
                        value={formData.honeySupers}
                        onChange={(e) => handleInputChange('honeySupers', parseInt(e.target.value) || 0)}
                        min="0"
                        max="10"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>🖼️ Frame Condition</label>
                      <select
                        value={formData.frameCondition}
                        onChange={(e) => handleInputChange('frameCondition', e.target.value)}
                      >
                        <option value="Excellent">Excellent - New or like new</option>
                        <option value="Good">Good - Minor wear, fully functional</option>
                        <option value="Fair">Fair - Some wear, needs attention</option>
                        <option value="Poor">Poor - Damaged, needs replacement</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Disease Check */}
              {currentStep === 4 && (
                <div className="form-step">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>🦠 Disease/Pest Detected</label>
                      <select
                        value={formData.disease}
                        onChange={(e) => handleInputChange('disease', e.target.value)}
                      >
                        <option value="">None detected</option>
                        <option value="AFB">⚠️ American Foulbrood (AFB)</option>
                        <option value="EFB">⚠️ European Foulbrood (EFB)</option>
                        <option value="Varroa destructor">🦠 Varroa destructor</option>
                        <option value="Nosema">🦠 Nosema</option>
                        <option value="Chalkbrood">🦠 Chalkbrood</option>
                        <option value="Small Hive Beetle">🪲 Small Hive Beetle</option>
                        <option value="Wax Moths">🦋 Wax Moths</option>
                        <option value="Other">❓ Other (specify in notes)</option>
                      </select>
                    </div>
                    
                    {formData.disease && (
                      <div className="form-group full-width">
                        <label>📝 Disease/Pest Notes</label>
                        <textarea
                          value={formData.diseaseNotes}
                          onChange={(e) => handleInputChange('diseaseNotes', e.target.value)}
                          placeholder="Describe symptoms, severity, location, treatment applied..."
                          rows={3}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 5: Management */}
              {currentStep === 5 && (
                <div className="form-step">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>💊 Treatment Applied</label>
                      <input
                        type="text"
                        value={formData.treatment}
                        onChange={(e) => handleInputChange('treatment', e.target.value)}
                        placeholder="e.g., Varroa strips, medication..."
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>🍯 Feeding</label>
                      <input
                        type="text"
                        value={formData.feeding}
                        onChange={(e) => handleInputChange('feeding', e.target.value)}
                        placeholder="e.g., Sugar syrup, pollen patties..."
                      />
                    </div>
                    
                    <div className="form-group full-width">
                      <label>🛠️ Equipment Changes</label>
                      <textarea
                        value={formData.equipment}
                        onChange={(e) => handleInputChange('equipment', e.target.value)}
                        placeholder="e.g., Added super, replaced frames, new entrance reducer..."
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 6: Documentation */}
              {currentStep === 6 && (
                <div className="form-step">
                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label>📸 Photo Evidence</label>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handlePhotoChange}
                        className="file-input"
                      />
                      {photoPreview && (
                        <div className="photo-preview">
                          <img src={photoPreview} alt="Inspection preview" />
                          <div className="photo-ai-actions">
                            <button 
                              onClick={() => setShowAIInspection(true)}
                              className="ai-analyze-photo-btn"
                            >
                              🤖 Analyze this Photo with AI
                            </button>
                            <p className="ai-analyze-hint">Get instant AI insights from your hive photo</p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="form-group full-width">
                      <label>🎤 Voice Recording</label>
                      <VoiceRecorder onSave={setVoiceBlob} disabled={submitting} />
                    </div>
                    
                    <div className="form-group full-width">
                      <label>📝 Additional Notes</label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => handleInputChange('notes', e.target.value)}
                        placeholder="Additional observations, plans for next inspection, concerns..."
                        rows={4}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="form-navigation">
              <div className="nav-buttons">
                <button 
                  type="button" 
                  onClick={onClose}
                  className="nav-btn cancel-btn"
                  disabled={submitting}
                >
                  ✕ Cancel
                </button>
                
                {currentStep > 1 && (
                  <button 
                    type="button" 
                    onClick={prevStep}
                    className="nav-btn prev-btn"
                  >
                    ← Previous
                  </button>
                )}
                
                {currentStep < steps.length ? (
                  <button 
                    type="button" 
                    onClick={nextStep}
                    className="nav-btn next-btn"
                  >
                    Next →
                  </button>
                ) : (
                  <button 
                    type="submit"
                    disabled={submitting}
                    className="nav-btn submit-btn"
                  >
                    {submitting ? 'Saving...' : '💾 Save Inspection'}
                  </button>
                )}
              </div>
              
              <div className="step-indicator">
                Step {currentStep} of {steps.length}
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EnhancedInspectionForm;